/**
 * CSV for lead import: parsing (quotes, commas and line breaks inside quotes,
 * CRLF) and turning rows into leads by their column names. Pure, so the import
 * screen and scripts/outreach-to-leads-csv.mjs agree on the format.
 */

export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ",") {
      row.push(cell);
      cell = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else cell += ch;
  }
  if (cell !== "" || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim() !== ""));
}

export type ImportRow = {
  kind: "LANDLORD" | "AGENT" | "HOUSING_OFFICE" | "OTHER";
  name: string;
  company?: string;
  email?: string;
  phone?: string;
  website?: string;
  area?: string;
  source?: string;
  listingUrl?: string;
  status: "NEW" | "CONTACTED" | "REPLIED" | "INTERESTED" | "SIGNED_UP" | "NOT_INTERESTED";
  notes?: string;
  nextFollowUpAt?: string | null;
};

/** Header names we understand, lower-cased, mapped to lead fields. */
const HEADERS: Record<string, keyof ImportRow> = {
  name: "name",
  "landlord name": "name",
  contact: "name",
  company: "company",
  school: "company",
  kind: "kind",
  type: "kind",
  email: "email",
  phone: "phone",
  website: "website",
  area: "area",
  "area / campus": "area",
  city: "area",
  campus: "area",
  source: "source",
  "listing link": "listingUrl",
  listing: "listingUrl",
  status: "status",
  notes: "notes",
  "follow-up date": "nextFollowUpAt",
  "next follow-up": "nextFollowUpAt",
};

const KINDS = ["LANDLORD", "AGENT", "HOUSING_OFFICE", "OTHER"] as const;
const STATUSES = ["NEW", "CONTACTED", "REPLIED", "INTERESTED", "SIGNED_UP", "NOT_INTERESTED"] as const;

/** The tracker's own wording, mapped onto CRM statuses. Anything unknown starts as NEW. */
function statusOf(raw: string): ImportRow["status"] {
  const v = raw
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  if ((STATUSES as readonly string[]).includes(v)) return v as ImportRow["status"];
  if (v === "TO_CONTACT" || v === "") return "NEW";
  if (v === "SENT" || v === "EMAILED" || v === "CALLED") return "CONTACTED";
  if (v === "LIVE" || v === "LISTED" || v === "SIGNED") return "SIGNED_UP";
  if (v === "NO" || v === "DECLINED") return "NOT_INTERESTED";
  return "NEW";
}

/**
 * Rows to leads. The first row is the header. A row without a name falls back
 * to its company; a row with neither, or a template "Example" row, is dropped.
 * Returns the leads and how many rows were dropped.
 */
export function rowsToLeads(rows: string[][], defaultKind: ImportRow["kind"] = "LANDLORD"): { leads: ImportRow[]; dropped: number } {
  const [header, ...body] = rows;
  if (!header) return { leads: [], dropped: 0 };
  const fields = header.map((h) => HEADERS[h.trim().toLowerCase()]);
  const leads: ImportRow[] = [];
  let dropped = 0;
  for (const cells of body) {
    const r: Record<string, string> = {};
    cells.forEach((c, i) => {
      const f = fields[i];
      if (f && c.trim() && !r[f]) r[f] = c.trim();
    });
    const name = (r.name || r.company || "").replace(/:$/, "").trim();
    if (!name || /^example\b/i.test(name)) {
      dropped++;
      continue;
    }
    const kind = (KINDS as readonly string[]).includes((r.kind ?? "").toUpperCase()) ? (r.kind!.toUpperCase() as ImportRow["kind"]) : defaultKind;
    const email = r.email && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(r.email) ? r.email : undefined;
    const date = r.nextFollowUpAt && /^\d{4}-\d{2}-\d{2}$/.test(r.nextFollowUpAt) ? r.nextFollowUpAt : undefined;
    const lead: ImportRow = { kind, name: name.slice(0, 200), status: statusOf(r.status ?? "") };
    for (const k of ["company", "phone", "website", "area", "source", "listingUrl", "notes"] as const) if (r[k]) lead[k] = r[k];
    // Keep an unusable email visible rather than losing it.
    if (r.email && !email) lead.notes = [lead.notes, `Email as given: ${r.email}`].filter(Boolean).join(" | ");
    if (email) lead.email = email;
    if (date) lead.nextFollowUpAt = date;
    leads.push(lead);
  }
  return { leads, dropped };
}
